/**
 * LeaderboardScreen.jsx - Standings V11 (V7 Foundation + V10 Improvements)
 * 
 * DESIGN: Mobile-first, dark theme, segmented sections
 * PURPOSE: Answer "Can I catch the leader?" AND "Who's hunting me?"
 * 
 * V11 FEATURES:
 * - Segmented sections: Ahead of You / You / Behind You
 * - Compact Hero: tappable summary → Field Report
 * - Gap language: "28 behind" / "+5 cushion" (not raw +/- numbers)
 * - Cushion colors: amber (hunting you) / green (safe)
 * - HUNTING badge: for opponents who can catch you
 * - Footer HUD: preserved from V7
 * 
 * NAVIGATION (LOCKED - unchanged from V7):
 * - Opponent row → H2H Scout (/standings/:poolId/vs/:opponentUserId)
 * - Your row → Field Report (/pools/:poolId/field-report)
 * - Back → War Room
 * 
 * VOCABULARY (BSL Compliant):
 * - "impact left" (not "ammo")
 * - "Hunting" / "Out of Reach" / "Not a Threat" / "Final"
 * - "Scout" (button label for H2H)
 * - "Coaches" (not Teams/Players/GMs)
 */

import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Hooks - using existing polling hook and auth context
import { useLeaderboardPolling } from '../../hooks/useLeaderboardPolling';
import { useAuth } from '../../context/AuthContext';

// =============================================================================
// ICONS (Inline SVG - unchanged from V7)
// =============================================================================

const ArrowLeftIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);

const CrownIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z" />
  </svg>
);

const RefreshIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const BinocularsIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

// =============================================================================
// THREAT STATUS LOGIC (unchanged from V7)
// =============================================================================

const getThreatStatus = (rank, score, impactLeft, leaderScore, isPoolCompleted = false) => {
  // Normalize impactLeft - use 0 if not a finite number
  const impact = Number.isFinite(impactLeft) ? impactLeft : 0;
  const gap = leaderScore - score;
  
  // If pool is completed, everyone is Final except the leader who is "In the Locker Room" (winner)
  if (isPoolCompleted) {
    if (rank === 1) {
      return { status: 'LOCKER_ROOM', label: 'In the Locker Room', color: 'text-amber-400' };
    }
    return { status: 'FINAL', label: 'Final', color: 'text-slate-500' };
  }
  
  // Leader logic
  if (rank === 1) {
    return impact === 0
      ? { status: 'LOCKER_ROOM', label: 'In the Locker Room', color: 'text-slate-400' }
      : { status: 'LEADER', label: 'Leader', color: 'text-amber-500' };
  }
  
  // Non-leader with no impact left = Final
  if (impact === 0) {
    return { status: 'FINAL', label: 'Final', color: 'text-slate-500' };
  }
  
  // Can they catch the leader?
  if (gap > impact) {
    return { status: 'OUT_OF_RANGE', label: 'Out of Range', color: 'text-slate-500' };
  }
  
  return { status: 'HUNTER', label: 'Hunting', color: 'text-emerald-400' };
};

// =============================================================================
// V11: COMPACT HERO COMPONENT
// =============================================================================

const CompactHero = ({ myStanding, myGap, myCanCatch, isPoolCompleted, onTap }) => {
  if (!myStanding) return null;
  
  const myImpactLeft = Number.isFinite(myStanding.impactLeft) ? myStanding.impactLeft : 0;
  
  // Determine verdict text
  let verdict = '';
  let verdictColor = 'text-slate-400';
  
  if (isPoolCompleted) {
    verdict = myStanding.rank === 1 ? '🏆 Victory!' : '🏁 Contest Complete';
    verdictColor = myStanding.rank === 1 ? 'text-amber-400' : 'text-slate-400';
  } else if (myStanding.rank === 1) {
    verdict = '👑 You\'re leading';
    verdictColor = 'text-amber-400';
  } else if (myCanCatch) {
    verdict = 'Still in the hunt';
    verdictColor = 'text-emerald-400';
  } else {
    verdict = 'Out of range';
    verdictColor = 'text-red-400';
  }
  
  return (
    <button
      onClick={onTap}
      className="w-full mx-3 mt-3 p-4 rounded-xl bg-indigo-900/30 border border-indigo-500/40 active:scale-[0.98] transition-all"
      style={{ width: 'calc(100% - 1.5rem)' }}
    >
      <div className="flex items-center justify-between">
        {/* Left: Rank + Gap */}
        <div className="flex items-center gap-3">
          <div className="text-3xl font-black text-indigo-400">#{myStanding.rank}</div>
          <div className="text-left">
            {myStanding.rank === 1 ? (
              <div className="text-lg font-mono font-black text-amber-400">{myStanding.score} pts</div>
            ) : (
              <div className="text-lg font-mono font-black text-red-400">{myGap} behind</div>
            )}
            <div className={`text-[12px] font-bold ${verdictColor}`}>{verdict}</div>
          </div>
        </div>
        
        {/* Right: Impact Left + Arrow */}
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10px] text-slate-500 uppercase font-bold">Impact Left</div>
            <div className={`text-xl font-mono font-black ${myCanCatch ? 'text-emerald-400' : 'text-slate-400'}`}>
              {myImpactLeft}
            </div>
          </div>
          <div className="text-indigo-400">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>
    </button>
  );
};

// =============================================================================
// V11: SECTION HEADER COMPONENT
// =============================================================================

const SectionHeader = ({ title, count, huntingCount }) => (
  <div className="flex items-center justify-between px-1 py-2 mt-4 mb-1">
    <div className="flex items-center gap-2">
      <span className="text-[11px] font-black text-slate-500 uppercase tracking-wider">{title}</span>
      <span className="text-[10px] text-slate-600">({count})</span>
    </div>
    {huntingCount > 0 && (
      <span className="text-[10px] font-bold text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full">
        🎯 {huntingCount} HUNTING
      </span>
    )}
  </div>
);

// =============================================================================
// STANDING ROW COMPONENT (V11 - updated gap language + hunting badge)
// =============================================================================

const StandingRow = ({ 
  coach, 
  leaderScore, 
  myScore,
  isPoolCompleted, 
  onScout, 
  onFieldReport 
}) => {
  const isLeader = coach.rank === 1;
  const isMe = coach.isMe;
  const gap = leaderScore - coach.score;
  
  // Normalize impactLeft for display and logic
  const impactLeft = Number.isFinite(coach.impactLeft) ? coach.impactLeft : 0;
  const threat = getThreatStatus(coach.rank, coach.score, impactLeft, leaderScore, isPoolCompleted);
  
  const isActive = threat.status === 'HUNTER' || threat.status === 'LEADER' || threat.status === 'LOCKER_ROOM';
  const isHunter = threat.status === 'HUNTER';
  
  // V11: Calculate if this coach is hunting ME (behind me but can catch me)
  const isHuntingMe = coach.isHuntingMe || false;
  
  // V11: Calculate cushion (how far behind me they are)
  const cushion = myScore - coach.score;
  const isBehindMe = cushion > 0;
  
  // Navigation logic (unchanged from V7)
  const handleTap = () => {
    if (isMe) {
      // Your own row → Field Report
      onFieldReport();
    } else {
      // Opponent row → Scout Report (head-to-head compare)
      onScout(coach);
    }
  };
  
  // V11: Determine cushion color
  // Amber = hunting me (danger), Green = safe (can't catch me)
  const getCushionColor = () => {
    if (!isBehindMe) return 'text-red-400'; // They're ahead
    if (isHuntingMe) return 'text-amber-400'; // Behind but can catch me
    return 'text-emerald-400'; // Safe cushion
  };
  
  return (
    <button
      onClick={handleTap}
      className={`w-full flex items-center p-3 rounded-xl border mb-2 transition-all active:scale-[0.98] min-h-[72px] ${
        isMe 
          ? 'bg-indigo-900/20 border-indigo-500/40' 
          : isLeader 
            ? 'bg-amber-900/10 border-amber-500/20'
            : isHuntingMe
              ? 'bg-amber-900/10 border-amber-500/30'
              : !isActive
                ? 'bg-slate-900/50 border-slate-800/50 opacity-60'
                : 'bg-slate-900 border-slate-800 hover:border-slate-700'
      }`}
    >
      {/* ZONE 1: RANK */}
      <div className="w-10 flex flex-col items-center justify-center shrink-0 mr-3">
        {isLeader ? (
          <div className="w-9 h-9 rounded-full bg-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
            <CrownIcon className="w-4 h-4 text-amber-950" />
          </div>
        ) : (
          <span className={`text-xl font-black ${isMe ? 'text-indigo-400' : 'text-slate-500'}`}>
            {coach.rank}
          </span>
        )}
      </div>

      {/* ZONE 2: IDENTITY (Name + Status + V11 Hunting Badge) */}
      <div className="flex-1 text-left min-w-0 mr-3">
        <div className="flex items-center gap-2">
          <span className={`text-[15px] font-bold truncate ${isMe ? 'text-indigo-300' : 'text-white'}`}>
            {isMe ? '👤 You' : coach.username}
          </span>
          {/* V11: HUNTING badge for opponents hunting me */}
          {!isMe && isHuntingMe && (
            <span className="text-[8px] font-black text-amber-400 bg-amber-500/20 px-1.5 py-0.5 rounded">
              🎯 HUNTING
            </span>
          )}
        </div>
        <div className={`text-[12px] font-medium mt-0.5 ${threat.color}`}>
          {threat.label}
        </div>
      </div>

      {/* ZONE 3: THE MATH (V11 - updated gap language) */}
      <div className="flex flex-col items-end shrink-0 min-w-[75px] mr-3">
        
        {/* V11: Gap language - "X behind" for ahead, "+X cushion" for behind */}
        <div className="flex items-baseline gap-1">
          {isLeader ? (
            <>
              <span className="text-lg font-mono font-black text-white">{coach.score}</span>
              <span className="text-[9px] text-amber-500 font-bold">pts</span>
            </>
          ) : isMe ? (
            <>
              <span className="text-lg font-mono font-black text-red-400">{gap}</span>
              <span className="text-[9px] text-slate-500">behind</span>
            </>
          ) : isBehindMe ? (
            <>
              <span className={`text-lg font-mono font-black ${getCushionColor()}`}>+{cushion}</span>
              <span className="text-[9px] text-slate-500">cushion</span>
            </>
          ) : (
            <>
              <span className="text-lg font-mono font-black text-red-400">{gap}</span>
              <span className="text-[9px] text-slate-500">behind</span>
            </>
          )}
        </div>
        
        {/* Impact Left */}
        <div className="flex items-baseline gap-1 mt-0.5">
          <span className={`text-lg font-mono font-black ${
            impactLeft === 0 ? 'text-slate-600' :
            isHunter ? 'text-emerald-400' : 
            'text-slate-400'
          }`}>
            {impactLeft}
          </span>
          <span className={`text-[9px] ${
            impactLeft === 0 ? 'text-slate-600' :
            isHunter ? 'text-emerald-500' : 
            'text-slate-500'
          }`}>
            left
          </span>
        </div>
      </div>

      {/* SCOUT REPORT / FIELD REPORT AFFORDANCE (unchanged from V7) */}
      <div className="flex flex-col items-center justify-center pl-3 border-l border-slate-800 min-w-[50px]">
        <BinocularsIcon className={`w-4 h-4 mb-0.5 ${isMe ? 'text-indigo-400' : 'text-slate-500'}`} />
        <span className={`text-[9px] font-bold ${isMe ? 'text-indigo-400' : 'text-slate-500'}`}>
          {isMe ? 'Report' : 'Scout'}
        </span>
      </div>
    </button>
  );
};

// =============================================================================
// MAIN COMPONENT (V11 - with segmentation)
// =============================================================================

export const LeaderboardScreen = ({ pool, entry, poolState, onBattleWhatIf, onShowToast }) => {
  const navigate = useNavigate();
  const { poolId: routePoolId } = useParams();
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Get poolId from route params (standalone) OR props (embedded in GM hub)
  // CRITICAL: This pattern MUST be preserved for standalone route to work
  const poolId = routePoolId || pool?.id || pool?.pool_id;
  
  // Get current user ID from auth hook
  const { user } = useAuth();
  const myUserId = String(user?.user_id ?? user?.id ?? '').toLowerCase();
  
  // Use polling hook (unchanged from V7)
  const { 
    standings: rawStandings, 
    poolInfo, 
    loading, 
    error, 
    refresh,
    lastUpdated
  } = useLeaderboardPolling(poolId);
  
  // Transform standings to include isMe flag and proper fields
  // Impact Left = balance/current_balance (remaining capacity)
  const standings = useMemo(() => {
    return rawStandings.map(s => {
      const oddsUserId = String(s.userId || s.user_id || '').toLowerCase();
      const isMe = myUserId && oddsUserId === myUserId;
      
      return {
        rank: s.rank,
        userId: s.userId || s.user_id,
        username: s.username || s.user_id,
        score: s.score ?? s.current_balance ?? 0,
        // Impact Left = balance/current_balance (this is remaining capacity)
        impactLeft: s.balance ?? s.current_balance ?? 0,
        totalUnits: s.totalUnits ?? s.total_units ?? null,
        isMe: isMe,
      };
    });
  }, [rawStandings, myUserId]);
  
  // Calculate derived values
  const leaderScore = standings.length > 0 ? standings[0].score : 0;
  const myStanding = standings.find(s => s.isMe);
  const myScore = myStanding?.score || 0;
  const myGap = leaderScore - myScore;
  const myImpactLeft = Number.isFinite(myStanding?.impactLeft) ? myStanding.impactLeft : 0;
  const myCanCatch = myStanding ? myGap <= myImpactLeft : null;
  
  // Determine if pool is completed (no more changes possible)
  const isPoolCompleted = poolState === 'COMPLETED' || poolState === 'completed';
  
  const coachCount = poolInfo?.totalEntrants || standings.length;
  
  // V11: Segment standings into Ahead / You / Behind
  const { ahead, behind, huntingCount } = useMemo(() => {
    if (!myStanding) {
      return { ahead: standings, behind: [], huntingCount: 0 };
    }
    
    const aheadList = [];
    const behindList = [];
    let hunting = 0;
    
    standings.forEach(coach => {
      if (coach.isMe) return; // Skip self
      
      const coachImpact = Number.isFinite(coach.impactLeft) ? coach.impactLeft : 0;
      const cushion = myScore - coach.score;
      
      // Is this coach hunting me? (behind me but can catch me)
      const isHuntingMe = cushion > 0 && coachImpact >= cushion;
      
      // Add hunting flag to coach
      const enrichedCoach = { ...coach, isHuntingMe };
      
      if (coach.score > myScore) {
        aheadList.push(enrichedCoach);
      } else if (coach.score < myScore) {
        behindList.push(enrichedCoach);
        if (isHuntingMe) hunting++;
      } else {
        // Same score - put in ahead (they have tiebreaker or we sort by rank)
        if (coach.rank < myStanding.rank) {
          aheadList.push(enrichedCoach);
        } else {
          behindList.push(enrichedCoach);
          if (isHuntingMe) hunting++;
        }
      }
    });
    
    return { ahead: aheadList, behind: behindList, huntingCount: hunting };
  }, [standings, myStanding, myScore]);
  
  // =========================================================================
  // NAVIGATION HANDLERS (CRITICAL - unchanged from V7)
  // =========================================================================
  
  const handleBack = () => {
    // Navigate to War Room (root)
    navigate('/');
  };
  
  const handleRefresh = () => {
    setIsRefreshing(true);
    refresh();
    setTimeout(() => setIsRefreshing(false), 1000);
  };
  
  // CRITICAL: This navigation pattern is LOCKED - do not modify
  const handleScout = (coach) => {
    // PHASE 4: Navigate to Head-to-Head Scoreboard (Architect LOCKED)
    navigate(`/standings/${poolId}/vs/${coach.userId}`);
  };
  
  // CRITICAL: This navigation pattern is LOCKED - do not modify
  const handleFieldReport = () => {
    // Navigate to Field Report
    navigate(`/pools/${poolId}/field-report`);
  };
  
  // =========================================================================
  // RENDER: Loading State
  // =========================================================================
  
  if (loading && !standings.length) {
    return (
      <div className="min-h-screen bg-slate-950 text-white font-sans flex items-center justify-center pb-20">
        <div className="text-center">
          <RefreshIcon className="w-8 h-8 animate-spin text-slate-400 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Loading standings...</p>
        </div>
      </div>
    );
  }
  
  // =========================================================================
  // RENDER: Error State
  // =========================================================================
  
  if (error && !standings.length) {
    return (
      <div className="min-h-screen bg-slate-950 text-white font-sans flex flex-col items-center justify-center p-4 pb-20">
        <p className="text-red-400 text-sm mb-4">{error}</p>
        <button 
          onClick={handleRefresh}
          className="px-4 py-2 bg-slate-800 rounded-lg text-white text-sm hover:bg-slate-700"
        >
          Try Again
        </button>
      </div>
    );
  }
  
  // =========================================================================
  // RENDER: Main Content
  // =========================================================================
  
  return (
    <div className="min-h-screen bg-slate-950 text-white font-sans pb-36">
      
      {/* ================================================ */}
      {/* HEADER */}
      {/* ================================================ */}
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-800">
        <div className="flex items-center justify-between px-3 py-3">
          <button 
            onClick={handleBack}
            className="flex items-center gap-2 text-slate-400 hover:text-white min-h-[44px]"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span className="text-[12px] font-bold uppercase">Back</span>
          </button>
          
          <div className="text-center">
            <h1 className="text-[12px] font-black text-white uppercase tracking-wider">Standings</h1>
            <p className="text-[11px] text-slate-500">{coachCount} Coaches</p>
          </div>
          
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-400 hover:text-white disabled:opacity-50"
          >
            <RefreshIcon className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
        
        {/* Last updated indicator */}
        {lastUpdated && (
          <div className="px-3 pb-2">
            <p className="text-[10px] text-slate-500 text-center">
              Updated {lastUpdated.toLocaleTimeString()}
            </p>
          </div>
        )}
      </header>

      {/* ================================================ */}
      {/* V11: COMPACT HERO (Tappable → Field Report) */}
      {/* ================================================ */}
      <CompactHero
        myStanding={myStanding}
        myGap={myGap}
        myCanCatch={myCanCatch}
        isPoolCompleted={isPoolCompleted}
        onTap={handleFieldReport}
      />

      {/* ================================================ */}
      {/* V11: SEGMENTED LIST */}
      {/* ================================================ */}
      <main className="px-3 pt-1">
        {standings.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-400 text-sm">No standings yet</p>
            <p className="text-slate-500 text-xs mt-1">Coaches will appear once rosters are locked</p>
          </div>
        ) : (
          <>
            {/* AHEAD OF YOU Section */}
            {ahead.length > 0 && (
              <>
                <SectionHeader title="Ahead of You" count={ahead.length} />
                {ahead.map(coach => (
                  <StandingRow 
                    key={coach.userId || coach.rank}
                    coach={coach}
                    leaderScore={leaderScore}
                    myScore={myScore}
                    isPoolCompleted={isPoolCompleted}
                    onScout={handleScout}
                    onFieldReport={handleFieldReport}
                  />
                ))}
              </>
            )}
            
            {/* YOUR POSITION Section */}
            {myStanding && (
              <>
                <SectionHeader title="Your Position" count={1} />
                <StandingRow 
                  key={myStanding.userId || myStanding.rank}
                  coach={myStanding}
                  leaderScore={leaderScore}
                  myScore={myScore}
                  isPoolCompleted={isPoolCompleted}
                  onScout={handleScout}
                  onFieldReport={handleFieldReport}
                />
              </>
            )}
            
            {/* BEHIND YOU Section */}
            {behind.length > 0 && (
              <>
                <SectionHeader title="Behind You" count={behind.length} huntingCount={huntingCount} />
                {behind.map(coach => (
                  <StandingRow 
                    key={coach.userId || coach.rank}
                    coach={coach}
                    leaderScore={leaderScore}
                    myScore={myScore}
                    isPoolCompleted={isPoolCompleted}
                    onScout={handleScout}
                    onFieldReport={handleFieldReport}
                  />
                ))}
              </>
            )}
            
            {/* Fallback: If user not in standings, show all */}
            {!myStanding && standings.length > 0 && (
              <>
                <SectionHeader title="All Coaches" count={standings.length} />
                {standings.map(coach => (
                  <StandingRow 
                    key={coach.userId || coach.rank}
                    coach={coach}
                    leaderScore={leaderScore}
                    myScore={0}
                    isPoolCompleted={isPoolCompleted}
                    onScout={handleScout}
                    onFieldReport={handleFieldReport}
                  />
                ))}
              </>
            )}
          </>
        )}
      </main>

      {/* ================================================ */}
      {/* STICKY FOOTER HUD (preserved from V7) */}
      {/* ================================================ */}
      {myStanding && (
        <div className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-xl border-t border-slate-700 shadow-[0_-10px_40px_rgba(0,0,0,0.5)] z-40">
          
          {/* VERDICT - Show for non-leaders (or completed pool status) */}
          {myStanding.rank !== 1 && (
            <div className={`text-center py-2 text-[11px] font-black uppercase tracking-wider ${
              isPoolCompleted
                ? 'bg-slate-500/10 text-slate-400 border-b border-slate-500/20'
                : myCanCatch 
                  ? 'bg-emerald-500/10 text-emerald-400 border-b border-emerald-500/20' 
                  : 'bg-red-500/10 text-red-400 border-b border-red-500/20'
            }`}>
              {isPoolCompleted 
                ? '🏁 Contest Complete'
                : myCanCatch 
                  ? '✓ Leader is in range' 
                  : '✗ Out of range'}
            </div>
          )}
          
          {/* Leader-specific verdict */}
          {myStanding.rank === 1 && (
            <div className={`text-center py-2 text-[11px] font-black uppercase tracking-wider ${
              isPoolCompleted
                ? 'bg-amber-500/10 text-amber-400 border-b border-amber-500/20'
                : 'bg-amber-500/10 text-amber-400 border-b border-amber-500/20'
            }`}>
              {isPoolCompleted ? '🏆 Victory!' : '👑 You\'re in the lead!'}
            </div>
          )}
          
          {/* YOUR POSITION */}
          <div className="px-4 py-4 flex items-center justify-between">
            
            {/* Rank + Name */}
            <div className="flex items-center gap-4">
              <div className="text-3xl font-black text-indigo-400">#{myStanding.rank}</div>
              <div>
                <div className="text-[11px] font-bold text-slate-500 uppercase">Your Position</div>
                <div className="text-[14px] font-bold text-white">{myStanding.username}</div>
              </div>
            </div>
            
            {/* Gap + Impact (V11: updated gap language) */}
            <div className="flex gap-5">
              {myStanding.rank === 1 ? (
                /* Leader sees their score instead of gap */
                <div className="text-right">
                  <div className="text-[10px] font-bold text-amber-400 uppercase mb-0.5">Score</div>
                  <div className="text-2xl font-mono font-black text-amber-400">{myStanding.score}</div>
                </div>
              ) : (
                /* Non-leaders see gap with "behind" language */
                <div className="text-right">
                  <div className="text-[10px] font-bold text-slate-500 uppercase mb-0.5">Gap</div>
                  <div className="text-2xl font-mono font-black text-red-400">{myGap} behind</div>
                </div>
              )}
              <div className="text-right">
                <div className="text-[10px] font-bold text-emerald-400 uppercase mb-0.5">Impact</div>
                <div className="text-2xl font-mono font-black text-emerald-400">
                  {myImpactLeft}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaderboardScreen;
